// src/utils/advancedCaching.js
// Advanced Caching Strategies for ADTAC Website

/**
 * ADVANCED CACHING STRATEGIES EXPLAINED:
 * 
 * 1. MULTI-LAYER CACHING:
 *    - Browser Cache: HTTP headers control client-side caching
 *    - Service Worker Cache: Programmable caching for offline support
 *    - CDN Cache: Content Delivery Network for global distribution
 *    - Memory Cache: In-memory caching for frequently accessed data
 * 
 * 2. CACHE STRATEGIES:
 *    - Cache First: Serve from cache, fallback to network
 *    - Network First: Try network, fallback to cache
 *    - Stale While Revalidate: Serve from cache, update in background
 *    - Network Only: Always fetch from network
 *    - Cache Only: Only serve from cache
 * 
 * 3. CACHE INVALIDATION:
 *    - Time-based expiration (TTL)
 *    - Version-based invalidation
 *    - Content hash-based updates
 *    - Manual cache busting
 */

class AdvancedCachingManager {
  constructor() {
    this.memoryCache = new Map();
    this.cacheStats = {
      hits: 0,
      misses: 0,
      size: 0
    };
    this.maxMemoryCacheSize = 50; // Max items in memory cache
    this.defaultTTL = 300000; // 5 minutes
    
    this.init();
  }

  init() {
    this.setupBrowserCacheHeaders();
    this.implementMemoryCache();
    this.setupCacheInvalidation();
    console.log('🚀 Advanced caching system initialized');
  }

  /**
   * BROWSER CACHE HEADERS CONFIGURATION
   * Sets up HTTP caching headers for different resource types
   */
  setupBrowserCacheHeaders() {
    const cacheHeadersConfig = {
      // Static Assets - Long-term caching (1 year)
      staticAssets: {
        'Cache-Control': 'public, max-age=31536000, immutable',
        'Expires': new Date(Date.now() + 31536000 * 1000).toUTCString()
      },
      
      // Images - Medium-term caching (1 month)
      images: {
        'Cache-Control': 'public, max-age=2592000',
        'Expires': new Date(Date.now() + 2592000 * 1000).toUTCString()
      },
      
      // HTML Pages - Short-term caching with revalidation (1 hour)
      html: {
        'Cache-Control': 'public, max-age=3600, must-revalidate',
        'Expires': new Date(Date.now() + 3600 * 1000).toUTCString()
      },
      
      // API Responses - Very short caching (5 minutes)
      api: {
        'Cache-Control': 'public, max-age=300, must-revalidate',
        'Expires': new Date(Date.now() + 300 * 1000).toUTCString()
      },
      
      // Fonts - Long-term caching (6 months)
      fonts: {
        'Cache-Control': 'public, max-age=15552000, immutable',
        'Expires': new Date(Date.now() + 15552000 * 1000).toUTCString()
      }
    };

    // Store configuration for reference
    this.cacheHeaders = cacheHeadersConfig;
    
    console.log('📋 Browser cache headers configured:');
    console.log('   • Static Assets: 1 year');
    console.log('   • Images: 1 month'); 
    console.log('   • HTML: 1 hour with revalidation');
    console.log('   • APIs: 5 minutes');
    console.log('   • Fonts: 6 months');
  }

  /**
   * MEMORY CACHE IMPLEMENTATION
   * Fast in-memory caching for frequently accessed data
   */
  implementMemoryCache() {
    // Memory cache for API responses and computed data
    this.memoryCache = new Map();
    
    // Cache access tracking for LRU eviction
    this.cacheAccessOrder = [];
    
    console.log('🧠 Memory cache system ready');
  }

  /**
   * SET MEMORY CACHE
   * Store data in memory with TTL
   */
  setMemoryCache(key, data, ttl = this.defaultTTL) {
    const cacheEntry = {
      data,
      timestamp: Date.now(),
      ttl,
      accessCount: 0
    };
    
    // Remove old entry if exists
    if (this.memoryCache.has(key)) {
      this.removeFromAccessOrder(key);
    }
    
    // Add new entry
    this.memoryCache.set(key, cacheEntry);
    this.cacheAccessOrder.unshift(key);
    
    // Enforce size limit (LRU eviction)
    if (this.memoryCache.size > this.maxMemoryCacheSize) {
      const oldestKey = this.cacheAccessOrder.pop();
      this.memoryCache.delete(oldestKey);
    }
    
    this.updateCacheStats();
    console.log(`💾 Cached: ${key} (TTL: ${ttl}ms)`);
  }

  /**
   * GET MEMORY CACHE
   * Retrieve data from memory cache
   */
  getMemoryCache(key) {
    const entry = this.memoryCache.get(key);
    
    if (!entry) {
      this.cacheStats.misses++;
      return null;
    }
    
    // Check if expired
    const isExpired = Date.now() - entry.timestamp > entry.ttl;
    if (isExpired) {
      this.memoryCache.delete(key);
      this.removeFromAccessOrder(key);
      this.cacheStats.misses++;
      return null;
    }
    
    // Update access tracking
    entry.accessCount++;
    this.moveToFront(key);
    this.cacheStats.hits++;
    
    console.log(`🎯 Cache hit: ${key}`);
    return entry.data;
  }

  /**
   * CACHE INVALIDATION STRATEGIES
   */
  setupCacheInvalidation() {
    // Time-based cache cleanup every 5 minutes
    setInterval(() => {
      this.cleanupExpiredCache();
    }, 300000);
    
    // Version-based invalidation
    this.currentVersion = this.getAppVersion();
    
    // Setup storage event listener for cross-tab cache invalidation
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key === 'cache-invalidate') {
          this.invalidateCache(event.newValue);
        }
      });
    }
    
    console.log('🔄 Cache invalidation system active');
  }

  /**
   * CLEANUP EXPIRED CACHE ENTRIES
   */
  cleanupExpiredCache() {
    const now = Date.now();
    let cleanedCount = 0;
    
    for (const [key, entry] of this.memoryCache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.memoryCache.delete(key);
        this.removeFromAccessOrder(key);
        cleanedCount++;
      }
    }
    
    if (cleanedCount > 0) {
      console.log(`🧹 Cleaned ${cleanedCount} expired cache entries`);
      this.updateCacheStats();
    }
  }

  /**
   * CDN CACHE CONFIGURATION
   * Configuration for Content Delivery Network caching
   */
  getCDNCacheConfig() {
    return {
      // Static assets with long-term caching
      staticAssets: {
        ttl: 31536000, // 1 year
        vary: ['Accept-Encoding'],
        customHeaders: {
          'Cache-Control': 'public, max-age=31536000, immutable'
        }
      },
      
      // Dynamic content with edge-side includes
      dynamicContent: {
        ttl: 3600, // 1 hour
        vary: ['Accept-Encoding', 'Accept-Language'],
        esi: true, // Edge Side Includes for personalization
        customHeaders: {
          'Cache-Control': 'public, max-age=3600, s-maxage=86400'
        }
      },
      
      // API endpoints with smart invalidation
      api: {
        ttl: 300, // 5 minutes
        vary: ['Accept-Encoding', 'Authorization'],
        purgeKeys: ['api-data', 'user-content'],
        customHeaders: {
          'Cache-Control': 'public, max-age=300, s-maxage=600'
        }
      }
    };
  }

  /**
   * INTELLIGENT CACHE WARMING
   * Proactively cache likely-to-be-requested content
   */
  async warmCache() {
    const criticalResources = [
      // Critical pages
      '/',
      '/consultancy-services',
      '/contact',
      
      // Critical API endpoints (if any)
      '/api/services',
      '/api/contact-info',
      
      // Critical images
      '/assets/images/logo/adtac-logo.png',
      '/assets/images/services/carousel-4-haccp.webp'
    ];
    
    console.log('🔥 Starting cache warming...');
    
    const warmingPromises = criticalResources.map(async (resource) => {
      try {
        if (resource.startsWith('/api/')) {
          // For API endpoints, just prepare the cache key
          this.setMemoryCache(`warm-${resource}`, { warmed: true }, 3600000);
        } else {
          // For static resources, they'll be cached by service worker
          // Just log the warming attempt
          console.log(`🌡️  Warming: ${resource}`);
        }
      } catch (error) {
        console.warn(`❌ Failed to warm ${resource}:`, error);
      }
    });
    
    await Promise.allSettled(warmingPromises);
    console.log('✅ Cache warming completed');
  }

  /**
   * CACHE PERFORMANCE MONITORING
   */
  getCachePerformanceStats() {
    const hitRate = this.cacheStats.hits / (this.cacheStats.hits + this.cacheStats.misses);
    
    return {
      memoryCache: {
        size: this.memoryCache.size,
        maxSize: this.maxMemoryCacheSize,
        hits: this.cacheStats.hits,
        misses: this.cacheStats.misses,
        hitRate: isNaN(hitRate) ? 0 : (hitRate * 100).toFixed(2) + '%'
      },
      serviceWorkerCache: this.getServiceWorkerCacheStats(),
      recommendations: this.getCacheRecommendations(hitRate)
    };
  }

  /**
   * CACHE RECOMMENDATIONS
   */
  getCacheRecommendations(hitRate) {
    const recommendations = [];
    
    if (hitRate < 0.7) {
      recommendations.push('Consider increasing cache TTL for frequently accessed data');
    }
    
    if (this.memoryCache.size >= this.maxMemoryCacheSize * 0.9) {
      recommendations.push('Memory cache near capacity, consider increasing maxSize');
    }
    
    if (this.cacheStats.misses > this.cacheStats.hits) {
      recommendations.push('High miss rate detected, review caching strategy');
    }
    
    return recommendations;
  }

  // Helper methods
  removeFromAccessOrder(key) {
    const index = this.cacheAccessOrder.indexOf(key);
    if (index > -1) {
      this.cacheAccessOrder.splice(index, 1);
    }
  }

  moveToFront(key) {
    this.removeFromAccessOrder(key);
    this.cacheAccessOrder.unshift(key);
  }

  updateCacheStats() {
    this.cacheStats.size = this.memoryCache.size;
  }

  getAppVersion() {
    // Get version from package.json or environment
    return process.env.REACT_APP_VERSION || '1.0.0';
  }

  getServiceWorkerCacheStats() {
    // This would integrate with service worker to get cache stats
    return {
      message: 'Service worker cache stats available in DevTools Application tab'
    };
  }

  invalidateCache(pattern) {
    let invalidatedCount = 0;
    
    for (const key of this.memoryCache.keys()) {
      if (key.includes(pattern)) {
        this.memoryCache.delete(key);
        this.removeFromAccessOrder(key);
        invalidatedCount++;
      }
    }
    
    console.log(`🗑️  Invalidated ${invalidatedCount} cache entries matching: ${pattern}`);
  }
}

// Initialize Advanced Caching
let advancedCachingManager;

export const initAdvancedCaching = () => {
  if (typeof window === 'undefined') return;
  
  advancedCachingManager = new AdvancedCachingManager();
  
  // Warm cache after initialization
  setTimeout(() => {
    advancedCachingManager.warmCache();
  }, 2000);
  
  return advancedCachingManager;
};

export const getCacheManager = () => advancedCachingManager;

// Export utilities for use in components
export const cacheUtils = {
  set: (key, data, ttl) => advancedCachingManager?.setMemoryCache(key, data, ttl),
  get: (key) => advancedCachingManager?.getMemoryCache(key),
  invalidate: (pattern) => advancedCachingManager?.invalidateCache(pattern),
  stats: () => advancedCachingManager?.getCachePerformanceStats()
};

export default AdvancedCachingManager;